import moment from "moment"

import { idify } from "../reducers/paging/paging-reducer-utils"
export { idify } from "../reducers/paging/paging-reducer-utils"

export function toUI(model, dateFields, tagFields) {
    if (model) {
        model = dateFields.reduce(
            (model, dateFieldName) => dateToUI(dateFieldName, model),
            model
        )
        model = tagFields.reduce(
            (model, tagFieldName) => tagsToUI(tagFieldName, model),
            model
        )
    }
    return model
}

export function toAPI(model, dateFields, tagFields) {
    if (model) {
        model = dateFields.reduce(
            (model, dateFieldName) => dateToAPI(dateFieldName, model),
            model
        )
        model = tagFields.reduce(
            (model, tagFieldName) => tagsToAPI(tagFieldName, model),
            model
        )
    }
    return model
}

export function dateToUI(dateFieldName, model) {
    const date = model.get(dateFieldName)
    return date ? model.set(dateFieldName, moment.unix(date)) : model
}

export function dateToAPI(dateFieldName, model) {
    const date = model.get(dateFieldName)
    return date ? model.set(dateFieldName, date.unix()) : model
}

export function tagsToUI(
    tagFieldName,
    model,
    valueKey = "id",
    labelKey = "name"
) {
    const tags = model.get(tagFieldName)
    if (tags) {
        const modifiedTags = tags.map((tag) => ({
            label: typeof tag === "string" ? tag : tag[labelKey],
            value: typeof tag === "string" ? tag : tag[valueKey],
        }))
        return model.set(tagFieldName, modifiedTags)
    }
    return model
}

export function tagsToAPI(
    tagFieldName,
    model,
    valueKey = "id",
    labelKey = "name"
) {
    const tags = model.get(tagFieldName)
    return tags
        ? model.set(
              tagFieldName,
              tags.map((tag) => tag[valueKey])
          )
        : model
}

export function mappingToAPI({ positive }) {
    return {
        positive: idify(positive),
    }
}

export function normaliseModel(entity, ...fieldsToNormalise) {
    return fieldsToNormalise.reduce(
        (entityData, field) => ({
            ...entityData,
            [field]: idify(entityData[field]),
        }),
        entity
    )
}

export function hydrateModel(entity, normalisingFields, entityStates) {
    return normalisingFields.reduce(
        (newEntity, normalisingField, index) => ({
            ...newEntity,
            [normalisingField]: (newEntity[normalisingField] || []).map(
                (id) => entityStates[index][id]
            ),
        }),
        entity
    )
}

function leftIntersection(list1, list2, itemComparator) {
    return list1.filter((list1Item) => {
        if (!itemComparator) {
            return list2.indexOf(list1Item) === -1
        }
        return !list2.some((list2Item) => itemComparator(list1Item, list2Item))
    })
}

function addRemoveEntity(
    prevList,
    entity,
    prevAddList,
    prevRemoveList,
    itemComparator
) {
    const existingItems = leftIntersection(prevList, [entity], itemComparator)
    const isRemoving = existingItems.length > 0
    const listToCheck = isRemoving ? prevAddList : prevRemoveList
    let listToAddTo = isRemoving ? prevRemoveList : prevAddList
    const listToCheckFiltered = listToCheck.filter(
        (addItem) => addItem !== entity
    )
    if (listToCheckFiltered.length === listToCheck.length)
        listToAddTo = [entity, ...listToAddTo]
    return {
        addList: isRemoving ? listToCheckFiltered : listToAddTo,
        removeList: isRemoving ? listToAddTo : listToCheckFiltered,
    }
}

function updateAddRemoveField(
    prevList,
    nextList,
    prevAddList,
    prevRemoveList,
    itemComparator
) {
    const addedItems = leftIntersection(nextList, prevList, itemComparator)
    const removedItems = leftIntersection(prevList, nextList, itemComparator)

    let addList = addedItems
    let removeList = removedItems
    if (addedItems && addedItems.length) {
        addList = [
            ...prevAddList,
            ...leftIntersection(addedItems, prevRemoveList, itemComparator),
        ]
        removeList = leftIntersection(
            prevRemoveList,
            addedItems,
            itemComparator
        )
    }
    if (removedItems && removedItems.length) {
        removeList = [
            ...prevRemoveList,
            ...leftIntersection(removedItems, prevAddList, itemComparator),
        ]
        addList = leftIntersection(prevAddList, removedItems, itemComparator)
    }

    return {
        addList,
        removeList,
    }
}

export function updateChangeModel(
    name,
    value,
    changeModel,
    workingModel,
    addRemoveFields = [],
    dateFields = []
) {
    const addFieldName = `${name}_add`
    const removeFieldName = `${name}_remove`
    const addRemove = addRemoveFields.filter(
        (item) => (item.name || item) === name
    )
    if (addRemove.length > 0) {
        let updateValue = value || []
        let previousValue = workingModel.get(name, [])
        const {
            comparator,
            transformAdd,
            transformRemove,
            transform,
        } = addRemove[0]
        if (!comparator) {
            updateValue = idify(updateValue)
            previousValue = idify(previousValue)
        }
        const updateFunction = Array.isArray(updateValue)
            ? updateAddRemoveField
            : addRemoveEntity
        let { addList, removeList } = updateFunction(
            previousValue,
            updateValue,
            changeModel.get(addFieldName) || [],
            changeModel.get(removeFieldName) || [],
            comparator
        )
        const transformAddFunction = transformAdd || transform
        if (transformAddFunction) {
            addList = transformAddFunction(addList)
        }
        const transformRemoveFunction = transformRemove || transform
        if (transformRemoveFunction) {
            removeList = transformRemoveFunction(removeList)
        }
        const items = [
            { list: addList, name: addFieldName },
            { list: removeList, name: removeFieldName },
        ]
        items.forEach(({ list, name }) => {
            if (list.length) {
                changeModel = changeModel.set(name, list)
            } else {
                changeModel = changeModel.remove(name)
            }
        })
        return changeModel
    } else if (dateFields.indexOf(name) !== -1) {
        if (value) {
            return changeModel.set(name, value.unix())
        } else {
            return changeModel.remove(name)
        }
    }
    return changeModel.set(name, (value && value.id) || value)
}

export function updateCreateModel(
    name,
    value,
    createModel,
    workingModel,
    addRemoveFields = [],
    dateFields = []
) {
    const addRemove = addRemoveFields.filter(
        (item) => (item.name || item) === name
    )
    if (addRemove.length > 0) {
        const { transform, transformAdd } = addRemove[0]
        const transformFunction = transformAdd || transform || idify
        value = value ? transformFunction(value) : []
    } else if (dateFields.indexOf(name) !== -1) {
        if (value) {
            value = value.unix()
        } else {
            return createModel.remove(name)
        }
    }
    return createModel.set(name, (value && value.id) || value)
}

export function isMappingSame(mapping1, mapping2) {
    if (mapping1.id && mapping2.id) {
        return mapping1.id === mapping2.id
    }
    return isEntityListSame(idify(mapping1.positive), idify(mapping2.positive))
}

function isEntityListSame(entityList1, entityList2) {
    entityList1 = entityList1 || []
    entityList2 = entityList2 || []
    const allIn1IsIn2 = leftExist(entityList1, entityList2)
    const allIn2IsIn1 = leftExist(entityList2, entityList1)
    return allIn1IsIn2 && allIn2IsIn1
}

function leftExist(list1, list2) {
    return !list1.some((item1) => list2.indexOf(item1) === -1)
}

export const PLACEHOLDER_ADD = "PLACEHOLDER_ADD"
export const PLACEHOLDER_UPDATE = "PLACEHOLDER_UPDATE"
export const PLACEHOLDER_REMOVE = "PLACEHOLDER_REMOVE"

export function updatePlaceholdersChangeModel(actionType, value, changeModel) {
    const idOrUrl = value && (value.id || value.url)
    switch (actionType) {
        case PLACEHOLDER_ADD: {
            const placeholdersRemove = changeModel.get(
                "placeholders_remove",
                []
            )
            const updatedPlaceholdersRemove = placeholdersRemove.filter(
                (id) => id !== idOrUrl
            )
            if (updatedPlaceholdersRemove.length < placeholdersRemove.length)
                return changeModel.set(
                    "placeholders_remove",
                    updatedPlaceholdersRemove
                )
            const placeholdersAdd = changeModel.get("placeholders_add", [])
            return changeModel.set(
                "placeholders_add",
                placeholdersAdd.concat([value])
            )
        }
        case PLACEHOLDER_UPDATE: {
            const arraysToCheck = ["placeholders_update", "placeholders_add"]
            let { attribution } = value
            // Normal for loop used to be able to return from function early
            for (let i = 0; i < arraysToCheck.length; i++) {
                const arrayName = arraysToCheck[i]
                const array = changeModel.get(arrayName, [])
                const existingUpdateItems = array.filter(
                    ({ id, url }) => (id || url) === idOrUrl
                )
                if (existingUpdateItems.length > 0) {
                    const updatedPlaceholdersUpdate = array
                        .filter(({ id, url }) => (id || url) !== idOrUrl)
                        .concat([
                            {
                                ...existingUpdateItems[0],
                                attribution,
                            },
                        ])
                    return changeModel.set(arrayName, updatedPlaceholdersUpdate)
                }
            }
            // Neither one contains this updated placeholder yet,
            // so add it to placeholders_update
            return changeModel.set(
                "placeholders_update",
                changeModel.get("placeholders_update", []).concat([
                    {
                        id: value.id,
                        attribution,
                    },
                ])
            )
        }
        case PLACEHOLDER_REMOVE: {
            const placeholdersAdd = changeModel.get("placeholders_add", [])
            const updatedPlaceholdersAdd = placeholdersAdd.filter(
                ({ id, url }) => (id || url) !== idOrUrl
            )
            if (updatedPlaceholdersAdd.length < placeholdersAdd.length)
                return changeModel.set(
                    "placeholders_add",
                    updatedPlaceholdersAdd
                )
            const placeholdersUpdate = changeModel.get(
                "placeholders_update",
                []
            )
            const updatedPlaceholdersUpdate = placeholdersUpdate.filter(
                ({ id }) => id !== idOrUrl
            )
            if (updatedPlaceholdersUpdate.length < placeholdersUpdate.length)
                changeModel = changeModel.set(
                    "placeholders_update",
                    updatedPlaceholdersUpdate
                )
            const placeholdersRemove = changeModel.get(
                "placeholders_remove",
                []
            )
            return changeModel.set(
                "placeholders_remove",
                placeholdersRemove.concat([value.id])
            )
        }
        default:
            return changeModel
    }
}
